// frontend/features/hr/hrSlice.js
import { createSlice } from "@reduxjs/toolkit";
import {
  fetchMyEmployeeProfile,
  fetchEmployeesList,
  updateEmployeeProfile,
  createEmployee,
  deleteEmployeeProfile,
  checkInTimeEntry,
  checkOutTimeEntry,
  fetchMyTimeEntries,
  fetchManagerTimeEntries,
  fetchMyLeaveRequests,
  createLeaveRequest,
  fetchManagerLeaveRequests,
  approveLeaveRequest,
  rejectLeaveRequest,
  updateManagerLeaveRequest,
  deleteManagerLeaveRequest,
  fetchWorkSchedule,
  updateWorkSchedule,
  fetchMyVacationSummary,
  fetchMyCalendar,
  fetchWorkSummaryReport,
  updateManagerTimeEntry,
  deleteManagerTimeEntry,
  createManagerLeaveRequest
} from "./hrThunks.js";


const initialState = {
  // Perfil e info básica
  employeeProfile: null,
  employeesList: [],

  loadingUpdateEmployeeProfile: false,
  errorUpdateEmployeeProfile: null,

  loadingCreateEmployee: false,
  errorCreateEmployee: null,

  loadingDeleteEmployeeProfile: false,
  errorDeleteEmployeeProfile: null,

  // Fichajes
  myTimeEntries: [],
  managerTimeEntries: [],
  lastTimeEntry: null,

  // Ausencias / vacaciones
  myLeaveRequests: [],
  managerLeaveRequests: [],
  myVacationSummary: null,
  myCalendar: null,

  // Horarios base
  workScheduleByEmployee: {}, // { [employeeId]: workSchedule }

  // Reportes
  workSummaryReport: null,

  // Loadings y errores
  loadingEmployeeProfile: false,
  errorEmployeeProfile: null,

  loadingEmployeesList: false,
  errorEmployeesList: null,

  loadingCheckIn: false,
  errorCheckIn: null,

  loadingCheckOut: false,
  errorCheckOut: null,

  loadingMyTimeEntries: false,
  errorMyTimeEntries: null,

  loadingManagerTimeEntries: false,
  errorManagerTimeEntries: null,

  loadingMyLeaveRequests: false,
  errorMyLeaveRequests: null,

  loadingCreateLeaveRequest: false,
  errorCreateLeaveRequest: null,

  loadingCreateManagerLeaveRequest: false,
  errorCreateManagerLeaveRequest: null,

  loadingManagerLeaveRequests: false,
  errorManagerLeaveRequests: null,

  loadingUpdateManagerLeaveRequest: false,
  errorUpdateManagerLeaveRequest: null,

  loadingDeleteManagerLeaveRequest: false,
  errorDeleteManagerLeaveRequest: null,

  loadingVacationSummary: false,
  errorVacationSummary: null,

  loadingCalendar: false,
  errorCalendar: null,

  loadingWorkSchedule: false,
  errorWorkSchedule: null,

  loadingUpdateWorkSchedule: false,
  errorUpdateWorkSchedule: null,

  loadingWorkSummaryReport: false,
  errorWorkSummaryReport: null,

  loadingManagerTimeEntries: false,
  errorManagerTimeEntries: null,

  loadingUpdateTimeEntry: false,
  errorUpdateTimeEntry: null,

  loadingDeleteTimeEntry: false,
  errorDeleteTimeEntry: null,

};

const hrSlice = createSlice({
  name: "hr",
  initialState,
  reducers: {
    clearHrErrors(state) {
      state.errorEmployeeProfile = null;
      state.errorEmployeesList = null;
      state.errorCreateEmployee = null;
      state.errorDeleteEmployeeProfile = null;
      state.errorCheckIn = null;
      state.errorCheckOut = null;
      state.errorMyTimeEntries = null;
      state.errorManagerTimeEntries = null;
      state.errorMyLeaveRequests = null;
      state.errorCreateLeaveRequest = null;
      state.errorManagerLeaveRequests = null;
      state.errorUpdateManagerLeaveRequest = null;
      state.errorDeleteManagerLeaveRequest = null;
      state.errorVacationSummary = null;
      state.errorCalendar = null;
      state.errorWorkSchedule = null;
      state.errorUpdateWorkSchedule = null;
      state.errorWorkSummaryReport = null;
      state.errorUpdateTimeEntry = null;
      state.errorDeleteTimeEntry = null;
      state.errorCreateManagerLeaveRequest = null;
      state.errorUpdateEmployeeProfile = null;
    },
    resetHrState() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    // ----- Perfil empleado -----
    builder
      .addCase(fetchMyEmployeeProfile.pending, (state) => {
        state.loadingEmployeeProfile = true;
        state.errorEmployeeProfile = null;
      })
      .addCase(fetchMyEmployeeProfile.fulfilled, (state, action) => {
        state.loadingEmployeeProfile = false;
        state.employeeProfile = action.payload;
      })
      .addCase(fetchMyEmployeeProfile.rejected, (state, action) => {
        state.loadingEmployeeProfile = false;
        state.errorEmployeeProfile =
          action.payload || "No se pudo cargar el perfil de empleado.";
      });

    // ----- Actualizar datos de empleado (manager) -----
    builder
      .addCase(updateEmployeeProfile.pending, (state) => {
        state.loadingUpdateEmployeeProfile = true;
        state.errorUpdateEmployeeProfile = null;
      })
      .addCase(updateEmployeeProfile.fulfilled, (state, action) => {
        state.loadingUpdateEmployeeProfile = false;
        const updated = action.payload;

        // Actualizar en la lista de empleados del manager
        if (state.employeesList && Array.isArray(state.employeesList)) {
          const index = state.employeesList.findIndex(
            (employee) => employee.id === updated.id
          );
          if (index !== -1) {
            state.employeesList[index] = updated;
          }
        }

        // Si el empleado actualizado es el propio perfil del usuario
        if (state.employeeProfile && state.employeeProfile.id === updated.id) {
          state.employeeProfile = updated;
        }
      })
      .addCase(updateEmployeeProfile.rejected, (state, action) => {
        state.loadingUpdateEmployeeProfile = false;
        state.errorUpdateEmployeeProfile =
          action.payload || "No se pudieron actualizar los datos del empleado.";
      });

    // ----- Eliminar empleado (soft delete) -----
    builder
      .addCase(deleteEmployeeProfile.pending, (state) => {
        state.loadingDeleteEmployeeProfile = true;
        state.errorDeleteEmployeeProfile = null;
      })
      .addCase(deleteEmployeeProfile.fulfilled, (state, action) => {
        state.loadingDeleteEmployeeProfile = false;
        const deletedEmployeeId = action.payload;

        if (state.employeesList && Array.isArray(state.employeesList)) {
          state.employeesList = state.employeesList.filter(
            (employee) => employee.id !== deletedEmployeeId
          );
        }

        if (state.employeeProfile && state.employeeProfile.id === deletedEmployeeId) {
          state.employeeProfile = null;
        }
      })
      .addCase(deleteEmployeeProfile.rejected, (state, action) => {
        state.loadingDeleteEmployeeProfile = false;
        state.errorDeleteEmployeeProfile =
          action.payload || "No se pudo eliminar el empleado.";
      });

    // ----- Lista empleados (manager) -----
    builder
      .addCase(fetchEmployeesList.pending, (state) => {
        state.loadingEmployeesList = true;
        state.errorEmployeesList = null;
      })
      .addCase(fetchEmployeesList.fulfilled, (state, action) => {
        state.loadingEmployeesList = false;
        state.employeesList = action.payload || [];
      })
      .addCase(fetchEmployeesList.rejected, (state, action) => {
        state.loadingEmployeesList = false;
        state.errorEmployeesList =
          action.payload || "No se pudo cargar la lista de empleados.";
      });

    builder
      .addCase(createEmployee.pending, (state) => {
        state.loadingCreateEmployee = true;
        state.errorCreateEmployee = null;
      })
      .addCase(createEmployee.fulfilled, (state, action) => {
        state.loadingCreateEmployee = false;
        const created = action.payload;
        if (!state.employeesList) state.employeesList = [];
        state.employeesList.unshift(created);
      })
      .addCase(createEmployee.rejected, (state, action) => {
        state.loadingCreateEmployee = false;
        state.errorCreateEmployee = action.payload || "No se pudo crear el empleado.";
      });

    // ----- Fichar entrada -----
    builder
      .addCase(checkInTimeEntry.pending, (state) => {
        state.loadingCheckIn = true;
        state.errorCheckIn = null;
      })
      .addCase(checkInTimeEntry.fulfilled, (state, action) => {
        state.loadingCheckIn = false;
        state.lastTimeEntry = action.payload;
        // opcional: añadir al principio de mis fichajes si ya están cargados
        if (state.myTimeEntries && Array.isArray(state.myTimeEntries)) {
          state.myTimeEntries.unshift(action.payload);
        }
      })
      .addCase(checkInTimeEntry.rejected, (state, action) => {
        state.loadingCheckIn = false;
        state.errorCheckIn =
          action.payload || "No se pudo fichar la entrada.";
      });

    // ----- Fichar salida -----
    builder
      .addCase(checkOutTimeEntry.pending, (state) => {
        state.loadingCheckOut = true;
        state.errorCheckOut = null;
      })
      .addCase(checkOutTimeEntry.fulfilled, (state, action) => {
        state.loadingCheckOut = false;
        state.lastTimeEntry = action.payload;

        // Actualizar el fichaje en myTimeEntries si existe
        const updatedEntry = action.payload;
        if (state.myTimeEntries && Array.isArray(state.myTimeEntries)) {
          const index = state.myTimeEntries.findIndex(
            (entry) => entry.id === updatedEntry.id
          );
          if (index !== -1) {
            state.myTimeEntries[index] = updatedEntry;
          } else {
            state.myTimeEntries.unshift(updatedEntry);
          }
        }
      })
      .addCase(checkOutTimeEntry.rejected, (state, action) => {
        state.loadingCheckOut = false;
        state.errorCheckOut =
          action.payload || "No se pudo fichar la salida.";
      });

    // ----- Mis fichajes -----
    builder
      .addCase(fetchMyTimeEntries.pending, (state) => {
        state.loadingMyTimeEntries = true;
        state.errorMyTimeEntries = null;
      })
      .addCase(fetchMyTimeEntries.fulfilled, (state, action) => {
        state.loadingMyTimeEntries = false;
        state.myTimeEntries = action.payload || [];
      })
      .addCase(fetchMyTimeEntries.rejected, (state, action) => {
        state.loadingMyTimeEntries = false;
        state.errorMyTimeEntries =
          action.payload || "No se pudieron cargar tus fichajes.";
      });

    // ----- Fichajes manager -----
    builder
      .addCase(fetchManagerTimeEntries.pending, (state) => {
        state.loadingManagerTimeEntries = true;
        state.errorManagerTimeEntries = null;
      })
      .addCase(fetchManagerTimeEntries.fulfilled, (state, action) => {
        state.loadingManagerTimeEntries = false;
        state.managerTimeEntries = action.payload || [];
      })
      .addCase(fetchManagerTimeEntries.rejected, (state, action) => {
        state.loadingManagerTimeEntries = false;
        state.errorManagerTimeEntries =
          action.payload || "No se pudieron cargar los fichajes (manager).";
      });

    // ----- Mis solicitudes de ausencias -----
    builder
      .addCase(fetchMyLeaveRequests.pending, (state) => {
        state.loadingMyLeaveRequests = true;
        state.errorMyLeaveRequests = null;
      })
      .addCase(fetchMyLeaveRequests.fulfilled, (state, action) => {
        state.loadingMyLeaveRequests = false;
        state.myLeaveRequests = action.payload || [];
      })
      .addCase(fetchMyLeaveRequests.rejected, (state, action) => {
        state.loadingMyLeaveRequests = false;
        state.errorMyLeaveRequests =
          action.payload ||
          "No se pudieron cargar tus solicitudes de ausencia.";
      });

    // ----- Crear solicitud de ausencia -----
    builder
      .addCase(createLeaveRequest.pending, (state) => {
        state.loadingCreateLeaveRequest = true;
        state.errorCreateLeaveRequest = null;
      })
      .addCase(createLeaveRequest.fulfilled, (state, action) => {
        state.loadingCreateLeaveRequest = false;
        const newRequest = action.payload;
        if (!state.myLeaveRequests) {
          state.myLeaveRequests = [];
        }
        state.myLeaveRequests.unshift(newRequest);
      })
      .addCase(createLeaveRequest.rejected, (state, action) => {
        state.loadingCreateLeaveRequest = false;
        state.errorCreateLeaveRequest =
          action.payload ||
          "No se pudo crear la solicitud de ausencia.";
      })

    // ----- Crear solicitud de ausencia como manager -----
    builder
      .addCase(createManagerLeaveRequest.pending, (state) => {
        state.loadingCreateManagerLeaveRequest = true;
        state.errorCreateManagerLeaveRequest = null;
      })
      .addCase(createManagerLeaveRequest.fulfilled, (state, action) => {
        state.loadingCreateManagerLeaveRequest = false;
        const newRequest = action.payload;

        if (!state.managerLeaveRequests) {
          state.managerLeaveRequests = [];
        }
        // la ponemos al principio de la lista de manager
        state.managerLeaveRequests.unshift(newRequest);
      })
      .addCase(createManagerLeaveRequest.rejected, (state, action) => {
        state.loadingCreateManagerLeaveRequest = false;
        state.errorCreateManagerLeaveRequest =
          action.payload ||
          "No se pudo crear la ausencia del empleado.";
      });

    // ----- Solicitudes de ausencias manager -----
    builder
      .addCase(fetchManagerLeaveRequests.pending, (state) => {
        state.loadingManagerLeaveRequests = true;
        state.errorManagerLeaveRequests = null;
      })
      .addCase(fetchManagerLeaveRequests.fulfilled, (state, action) => {
        state.loadingManagerLeaveRequests = false;
        state.managerLeaveRequests = action.payload || [];
      })
      .addCase(fetchManagerLeaveRequests.rejected, (state, action) => {
        state.loadingManagerLeaveRequests = false;
        state.errorManagerLeaveRequests =
          action.payload ||
          "No se pudieron cargar las solicitudes de ausencia (manager).";
      });

    // ----- Aprobar solicitud de ausencia -----
    builder
      .addCase(approveLeaveRequest.pending, (state) => {
        // no hace falta loading global aparte
      })
      .addCase(approveLeaveRequest.fulfilled, (state, action) => {
        const updated = action.payload;
        // Actualizamos managerLeaveRequests
        if (state.managerLeaveRequests && Array.isArray(state.managerLeaveRequests)) {
          const index = state.managerLeaveRequests.findIndex(
            (req) => req.id === updated.id
          );
          if (index !== -1) {
            state.managerLeaveRequests[index] = updated;
          }
        }
        // Actualizamos myLeaveRequests por si el manager ve sus propias solicitudes
        if (state.myLeaveRequests && Array.isArray(state.myLeaveRequests)) {
          const index = state.myLeaveRequests.findIndex(
            (req) => req.id === updated.id
          );
          if (index !== -1) {
            state.myLeaveRequests[index] = updated;
          }
        }
      })
      .addCase(approveLeaveRequest.rejected, (state, action) => {
        state.errorManagerLeaveRequests =
          action.payload ||
          "No se pudo aprobar la solicitud de ausencia.";
      });

    // ----- Rechazar solicitud de ausencia -----
    builder
      .addCase(rejectLeaveRequest.pending, (state) => {
        // no hace falta loading global aparte
      })
      .addCase(rejectLeaveRequest.fulfilled, (state, action) => {
        const updated = action.payload;
        if (state.managerLeaveRequests && Array.isArray(state.managerLeaveRequests)) {
          const index = state.managerLeaveRequests.findIndex(
            (req) => req.id === updated.id
          );
          if (index !== -1) {
            state.managerLeaveRequests[index] = updated;
          }
        }
        if (state.myLeaveRequests && Array.isArray(state.myLeaveRequests)) {
          const index = state.myLeaveRequests.findIndex(
            (req) => req.id === updated.id
          );
          if (index !== -1) {
            state.myLeaveRequests[index] = updated;
          }
        }
      })
      .addCase(rejectLeaveRequest.rejected, (state, action) => {
        state.errorManagerLeaveRequests =
          action.payload ||
          "No se pudo rechazar la solicitud de ausencia.";
      });

    // ----- Actualizar solicitud de ausencia (manager) -----
    builder
      .addCase(updateManagerLeaveRequest.pending, (state) => {
        state.loadingUpdateManagerLeaveRequest = true;
        state.errorUpdateManagerLeaveRequest = null;
      })
      .addCase(updateManagerLeaveRequest.fulfilled, (state, action) => {
        state.loadingUpdateManagerLeaveRequest = false;
        const updated = action.payload;

        if (state.managerLeaveRequests && Array.isArray(state.managerLeaveRequests)) {
          const index = state.managerLeaveRequests.findIndex((req) => req.id === updated.id);
          if (index !== -1) state.managerLeaveRequests[index] = updated;
        }

        if (state.myLeaveRequests && Array.isArray(state.myLeaveRequests)) {
          const index = state.myLeaveRequests.findIndex((req) => req.id === updated.id);
          if (index !== -1) state.myLeaveRequests[index] = updated;
        }
      })
      .addCase(updateManagerLeaveRequest.rejected, (state, action) => {
        state.loadingUpdateManagerLeaveRequest = false;
        state.errorUpdateManagerLeaveRequest =
          action.payload || "No se pudo actualizar la solicitud de ausencia.";
      });

    // ----- Eliminar solicitud de ausencia (manager) -----
    builder
      .addCase(deleteManagerLeaveRequest.pending, (state) => {
        state.loadingDeleteManagerLeaveRequest = true;
        state.errorDeleteManagerLeaveRequest = null;
      })
      .addCase(deleteManagerLeaveRequest.fulfilled, (state, action) => {
        state.loadingDeleteManagerLeaveRequest = false;
        const deletedId = action.payload;

        if (state.managerLeaveRequests && Array.isArray(state.managerLeaveRequests)) {
          state.managerLeaveRequests = state.managerLeaveRequests.filter((req) => req.id !== deletedId);
        }

        if (state.myLeaveRequests && Array.isArray(state.myLeaveRequests)) {
          state.myLeaveRequests = state.myLeaveRequests.filter((req) => req.id !== deletedId);
        }
      })
      .addCase(deleteManagerLeaveRequest.rejected, (state, action) => {
        state.loadingDeleteManagerLeaveRequest = false;
        state.errorDeleteManagerLeaveRequest =
          action.payload || "No se pudo eliminar la solicitud de ausencia.";
      });

    // ----- Horario base empleado -----
    builder
      .addCase(fetchWorkSchedule.pending, (state) => {
        state.loadingWorkSchedule = true;
        state.errorWorkSchedule = null;
      })
      .addCase(fetchWorkSchedule.fulfilled, (state, action) => {
        state.loadingWorkSchedule = false;
        const { employeeId, workSchedule } = action.payload;
        state.workScheduleByEmployee[employeeId] = workSchedule;
      })
      .addCase(fetchWorkSchedule.rejected, (state, action) => {
        state.loadingWorkSchedule = false;
        state.errorWorkSchedule =
          action.payload || "No se pudo cargar el horario base.";
      });

    builder
      .addCase(updateWorkSchedule.pending, (state) => {
        state.loadingUpdateWorkSchedule = true;
        state.errorUpdateWorkSchedule = null;
      })
      .addCase(updateWorkSchedule.fulfilled, (state, action) => {
        state.loadingUpdateWorkSchedule = false;
        const { employeeId, workSchedule } = action.payload;
        state.workScheduleByEmployee[employeeId] = workSchedule;
      })
      .addCase(updateWorkSchedule.rejected, (state, action) => {
        state.loadingUpdateWorkSchedule = false;
        state.errorUpdateWorkSchedule =
          action.payload || "No se pudo actualizar el horario base.";
      });

    // ----- Resumen de vacaciones -----
    builder
      .addCase(fetchMyVacationSummary.pending, (state) => {
        state.loadingVacationSummary = true;
        state.errorVacationSummary = null;
      })
      .addCase(fetchMyVacationSummary.fulfilled, (state, action) => {
        state.loadingVacationSummary = false;
        state.myVacationSummary = action.payload;
      })
      .addCase(fetchMyVacationSummary.rejected, (state, action) => {
        state.loadingVacationSummary = false;
        state.errorVacationSummary =
          action.payload ||
          "No se pudo cargar el resumen de vacaciones.";
      });

    // ----- Calendario anual -----
    builder
      .addCase(fetchMyCalendar.pending, (state) => {
        state.loadingCalendar = true;
        state.errorCalendar = null;
      })
      .addCase(fetchMyCalendar.fulfilled, (state, action) => {
        state.loadingCalendar = false;
        state.myCalendar = action.payload; // { year, events }
      })
      .addCase(fetchMyCalendar.rejected, (state, action) => {
        state.loadingCalendar = false;
        state.errorCalendar =
          action.payload || "No se pudo cargar el calendario anual.";
      });

    // ----- Reporte de trabajo -----
    builder
      .addCase(fetchWorkSummaryReport.pending, (state) => {
        state.loadingWorkSummaryReport = true;
        state.errorWorkSummaryReport = null;
      })
      .addCase(fetchWorkSummaryReport.fulfilled, (state, action) => {
        state.loadingWorkSummaryReport = false;
        state.workSummaryReport = action.payload;
      })
      .addCase(fetchWorkSummaryReport.rejected, (state, action) => {
        state.loadingWorkSummaryReport = false;
        state.errorWorkSummaryReport =
          action.payload || "No se pudo cargar el reporte de trabajo.";
      })

    // ----- Actualizar fichaje (manager) -----
    builder
      .addCase(updateManagerTimeEntry.pending, (state) => {
        state.loadingUpdateTimeEntry = true;
        state.errorUpdateTimeEntry = null;
      })
      .addCase(updateManagerTimeEntry.fulfilled, (state, action) => {
        state.loadingUpdateTimeEntry = false;
        const updated = action.payload;

        if (state.managerTimeEntries && Array.isArray(state.managerTimeEntries)) {
          const index = state.managerTimeEntries.findIndex(
            (entry) => entry.id === updated.id
          );
          if (index !== -1) {
            state.managerTimeEntries[index] = updated;
          }
        }

        if (state.myTimeEntries && Array.isArray(state.myTimeEntries)) {
          const index = state.myTimeEntries.findIndex(
            (entry) => entry.id === updated.id
          );
          if (index !== -1) {
            state.myTimeEntries[index] = updated;
          }
        }
      })
      .addCase(updateManagerTimeEntry.rejected, (state, action) => {
        state.loadingUpdateTimeEntry = false;
        state.errorUpdateTimeEntry =
          action.payload || "No se pudo actualizar el fichaje.";
      });

    // ----- Eliminar fichaje (manager) -----
    builder
      .addCase(deleteManagerTimeEntry.pending, (state) => {
        state.loadingDeleteTimeEntry = true;
        state.errorDeleteTimeEntry = null;
      })
      .addCase(deleteManagerTimeEntry.fulfilled, (state, action) => {
        state.loadingDeleteTimeEntry = false;
        const deletedId = action.payload;

        if (state.managerTimeEntries && Array.isArray(state.managerTimeEntries)) {
          state.managerTimeEntries = state.managerTimeEntries.filter(
            (entry) => entry.id !== deletedId
          );
        }

        if (state.myTimeEntries && Array.isArray(state.myTimeEntries)) {
          state.myTimeEntries = state.myTimeEntries.filter(
            (entry) => entry.id !== deletedId
          );
        }
      })
      .addCase(deleteManagerTimeEntry.rejected, (state, action) => {
        state.loadingDeleteTimeEntry = false;
        state.errorDeleteTimeEntry =
          action.payload || "No se pudo eliminar el fichaje.";
      });
  },
});

export const { clearHrErrors, resetHrState } = hrSlice.actions;
export default hrSlice.reducer;